import type { UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";

export interface UsersAccessQuerySlice {
  data?: Record<string, UserAccessEntry[]>;
  isPlaceholderData: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
}

export type UserAccessPhase = "ready" | "loading" | "error";

function hasEntry(
  data: Record<string, UserAccessEntry[]> | undefined,
  userId: string,
): data is Record<string, UserAccessEntry[]> {
  return !!data && Object.prototype.hasOwnProperty.call(data, userId);
}

/**
 * Joins batched access queries into one map keyed by user id.
 *
 * Opening a person from an org resets the people list, which changes the
 * access-chunk query key. Until that request settles, keep the last known
 * orgs so the drawer does not paint 0. A settled empty array still means
 * the user has no organizations.
 */
export function mergeUsersAccess(
  userIds: readonly string[],
  chunks: readonly (readonly string[])[],
  queries: readonly UsersAccessQuerySlice[],
  previous: ReadonlyMap<string, UserAccessEntry[]>,
): Map<string, UserAccessEntry[]> {
  const map = new Map<string, UserAccessEntry[]>();

  for (const userId of userIds) {
    const index = chunks.findIndex((ids) => ids.includes(userId));
    const query = index >= 0 ? queries[index] : undefined;
    const hasSettledData = !!query
      && !query.isPlaceholderData
      && !query.isLoading
      && query.data !== undefined;

    if (hasSettledData) {
      map.set(userId, hasEntry(query.data, userId) ? query.data[userId] : []);
      continue;
    }

    if (query?.isPlaceholderData && hasEntry(query.data, userId)) {
      map.set(userId, query.data[userId]);
      continue;
    }

    const prior = previous.get(userId);
    const isPending = !query
      || query.isLoading
      || query.isFetching
      || query.isPlaceholderData
      || query.data === undefined;
    if (prior && isPending) {
      map.set(userId, prior);
    }
  }

  return map;
}

/** Ready when orgs are known. Error when this user's chunk failed and nothing is cached. */
export function userAccessPhase(
  userId: string,
  chunks: readonly (readonly string[])[],
  queries: readonly UsersAccessQuerySlice[],
  accessByUser: ReadonlyMap<string, UserAccessEntry[]>,
): UserAccessPhase {
  if (accessByUser.has(userId)) return "ready";
  const index = chunks.findIndex((ids) => ids.includes(userId));
  const query = index >= 0 ? queries[index] : undefined;
  if (query?.isError) return "error";
  return "loading";
}

/** Copies settled per-user access into `previous` so the next in-flight chunk can reuse it. */
export function rememberSettledUsersAccess(
  chunks: readonly (readonly string[])[],
  queries: readonly UsersAccessQuerySlice[],
  previous: Map<string, UserAccessEntry[]>,
): void {
  chunks.forEach((ids, index) => {
    const query = queries[index];
    if (!query?.data || query.isPlaceholderData || query.isLoading) return;
    for (const userId of ids) {
      if (hasEntry(query.data, userId)) {
        previous.set(userId, query.data[userId]);
      }
    }
  });
}
